import { CommonModule, formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { UserContextProvider } from '../../infrastructure/context/user-context-provider';
import { Logger } from '../../infrastructure/logging/logger';
import { NavService } from '../../layout/nav/nav.service';
import { ActiveEventMessage, DataTableFilterMessageService } from '../../shared/components/data-table/filters/components/data-table-filter-message-service';
import { DataTableFilterComponent } from '../../shared/components/data-table/filters/components/data-table-filter/data-table-filter.component';
import { DataTableFilterPillComponent, DataTableFilterPillState } from '../../shared/components/data-table/filters/components/data-table-filter-pill/data-table-filter-pill.component';
import { DataTableFilter } from '../../shared/components/data-table/filters/components/DataTableFilter';
import { dateFormat } from '../../utilities/DateUtility';
import { NotificationService } from '../../utilities/PortalNotificationUtility';
import { MarkPassedConfirmationDialogComponent } from './components/mark-passed-confirmation-dialog/mark-passed-confirmation-dialog.component';
import { ScreeningReportDateFilterComponent } from './components/screening-report-date-filter/screening-report-date-filter.component';
import { ScreeningReviewPanelComponent } from './components/screening-review-panel/screening-review-panel.component';
import { ReviewAction } from './models/ReviewAction';
import { ScreeningReportDateField } from './models/ScreeningReportDateField';
import { ScreeningReportQueue } from './models/ScreeningReportQueue';
import { ScreeningReportRow } from './models/ScreeningReportRow';
import { ScreeningReportSearchRequest } from './models/ScreeningReportSearchRequest';
import { ScreeningReportService, toApiDate } from './services/screening-report.service';
import { ARRIVAL_DATE_FILTER_ID, DEPARTURE_DATE_FILTER_ID, ScreeningReportFilterStateService } from './services/screening-report-filter-state.service';

interface AppliedDateFilter {
  dateField: ScreeningReportDateField;
  from?: string;
  to?: string;
}

// Arrival is "on or after" and departure "on or before", matching the Reservations page filters.
const DATE_FILTERS: Record<string, { dateField: ScreeningReportDateField; bound: 'from' | 'to' }> = {
  [ARRIVAL_DATE_FILTER_ID]: { dateField: ScreeningReportDateField.ARRIVAL, bound: 'from' },
  [DEPARTURE_DATE_FILTER_ID]: { dateField: ScreeningReportDateField.DEPARTURE, bound: 'to' }
};

const SORT_COLUMN_TO_DATE_FIELD: Record<string, ScreeningReportDateField> = {
  dates: ScreeningReportDateField.ARRIVAL,
  completed: ScreeningReportDateField.COMPLETED,
  reviewedAt: ScreeningReportDateField.REVIEWED
};

const DATE_FIELD_TO_SORT_COLUMN: Partial<Record<ScreeningReportDateField, string>> = {
  [ScreeningReportDateField.ARRIVAL]: 'dates',
  [ScreeningReportDateField.COMPLETED]: 'completed',
  [ScreeningReportDateField.REVIEWED]: 'reviewedAt'
};

const NEEDS_REVIEW_COLUMNS = ['guest', 'organization', 'dates', 'completed'];
const REVIEWED_COLUMNS = [...NEEDS_REVIEW_COLUMNS, 'reviewedAt'];

@Component({
  selector: 'app-screening-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    DataTableFilterComponent,
    DataTableFilterPillComponent,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    ScreeningReviewPanelComponent
  ],
  providers: [provideNativeDateAdapter(), ScreeningReportFilterStateService],
  templateUrl: './screening-report.component.html',
  styleUrl: './screening-report.component.scss'
})
export class ScreeningReportComponent implements OnInit, OnDestroy {

  readonly queues = ScreeningReportQueue;
  readonly pageSizeOptions = [10, 25, 50, 100];

  readonly filterDefinitions: DataTableFilter[] = [
    { id: ARRIVAL_DATE_FILTER_ID, name: 'Arrival Date', shortName: 'Arrival Date', component: ScreeningReportDateFilterComponent },
    { id: DEPARTURE_DATE_FILTER_ID, name: 'Departure Date', shortName: 'Departure Date', component: ScreeningReportDateFilterComponent }
  ];
  readonly loading$ = new BehaviorSubject<boolean>(false);

  queue: ScreeningReportQueue = ScreeningReportQueue.NEEDS_REVIEW;
  filterPills = new Map<string, DataTableFilterPillState>();
  searchKeyword = '';

  rows: ScreeningReportRow[] = [];
  totalRecords = 0;
  pageIndex = 0;
  pageSize = 25;
  sortField: ScreeningReportDateField = ScreeningReportDateField.COMPLETED;
  sortDirection: 'ASC' | 'DESC' = 'DESC';

  selectedRow?: ScreeningReportRow;
  pendingAction: ReviewAction | null = null;

  organizationId?: string;
  selectedOrganizationName = '';

  private appliedDateFilter?: AppliedDateFilter;
  private appliedKeyword = '';
  private latestRequestId = 0;
  private subscriptions = new Subscription();

  constructor(
    private screeningReportService: ScreeningReportService,
    private navService: NavService,
    private userContextProvider: UserContextProvider,
    private logService: Logger,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private filterState: ScreeningReportFilterStateService,
    private filterMessageService: DataTableFilterMessageService
  ) { }

  get loading(): boolean {
    return this.loading$.value;
  }

  get tableColumns(): string[] {
    return this.queue === ScreeningReportQueue.REVIEWED ? REVIEWED_COLUMNS : NEEDS_REVIEW_COLUMNS;
  }

  get sortColumn(): string {
    return DATE_FIELD_TO_SORT_COLUMN[this.sortField] ?? 'completed';
  }

  ngOnInit(): void {
    this.subscriptions.add(this.navService.selectedOrg$.subscribe((org: any) => {
      this.organizationId = org?.organization_id != null ? `${org.organization_id}` : undefined;
      this.selectedOrganizationName = org?.organization_name ?? '';
      this.pageIndex = 0;
      this.selectedRow = undefined;
      this.loadPage();
    }));
    this.subscriptions.add(this.filterState.changed.subscribe(filterId => this.onDateFilterChanged(filterId)));
    this.subscriptions.add(this.filterMessageService.activeEvent.subscribe((event: ActiveEventMessage) => this.updateFilterPill(event)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setQueue = async (queue: ScreeningReportQueue) => {
    this.queue = queue;
    this.sortField = queue === ScreeningReportQueue.REVIEWED
      ? ScreeningReportDateField.REVIEWED
      : ScreeningReportDateField.COMPLETED;
    this.sortDirection = 'DESC';
    this.pageIndex = 0;
    this.selectedRow = undefined;
    await this.loadPage();
  }

  onDateFilterChanged = async (filterId: string) => {
    const filter = DATE_FILTERS[filterId];
    const date = this.filterState.dates[filterId];
    if (date) {
      // The report API filters on one date field at a time, so applying one date filter replaces the other.
      for (const otherId of Object.keys(DATE_FILTERS).filter(id => id !== filterId)) {
        this.filterState.dates[otherId] = null;
        this.filterMessageService.setActive({ filterId: otherId, active: false });
      }
      this.appliedDateFilter = { dateField: filter.dateField, [filter.bound]: toApiDate(date) };
      this.filterMessageService.setActive({ filterId, active: true, value: formatDate(date, 'MMM d, y', 'en-US') });
    } else {
      this.filterMessageService.setActive({ filterId, active: false });
      if (this.appliedDateFilter?.dateField !== filter.dateField) {
        return;
      }
      this.appliedDateFilter = undefined;
    }
    this.pageIndex = 0;
    this.selectedRow = undefined;
    await this.loadPage();
  }

  onSearch = async () => {
    this.appliedKeyword = this.searchKeyword.trim();
    this.pageIndex = 0;
    this.selectedRow = undefined;
    await this.loadPage();
  }

  // Like the shared table: backspacing the box to empty clears an applied search.
  onSearchBackspace = async () => {
    if (this.searchKeyword === '' && this.appliedKeyword !== '') {
      await this.onSearch();
    }
  }

  clearSearch = async () => {
    this.searchKeyword = '';
    await this.onSearch();
  }

  onFilterPillClose(filterId: string): void {
    this.filterMessageService.onClearClicked(filterId);
  }

  onPage = async (event: PageEvent) => {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.selectedRow = undefined;
    await this.loadPage();
  }

  onSort = async (sort: Sort) => {
    if (sort.direction) {
      this.sortField = SORT_COLUMN_TO_DATE_FIELD[sort.active] ?? ScreeningReportDateField.COMPLETED;
      this.sortDirection = sort.direction === 'asc' ? 'ASC' : 'DESC';
    } else {
      this.sortField = ScreeningReportDateField.COMPLETED;
      this.sortDirection = 'DESC';
    }
    this.pageIndex = 0;
    this.selectedRow = undefined;
    await this.loadPage();
  }

  onRowClick(row: ScreeningReportRow): void {
    this.selectedRow = row;
  }

  onKeepAsConsider = async (row: ScreeningReportRow) => {
    await this.runReviewAction(row, 'keep', () => this.screeningReportService.markReviewed(row.screeningId), 'Kept as Consider');
  }

  onMarkAsPassed = async (row: ScreeningReportRow) => {
    const confirmed = await firstValueFrom(this.dialog.open(MarkPassedConfirmationDialogComponent, {
      width: '500px',
      data: { guestName: `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() }
    }).afterClosed());
    if (!confirmed) {
      return;
    }
    await this.runReviewAction(row, 'pass', () => this.screeningReportService.markPassed(row.screeningId), 'Marked as Passed', {
      429: 'AutoHost is busy, so nothing was changed. Try again in a minute.',
      502: "AutoHost couldn't approve this reservation, so nothing was changed. Try again.",
      504: "AutoHost didn't confirm the approval, so the screening wasn't passed. Check the reservation in AutoHost, then try again."
    });
  }

  onReturnToQueue = async (row: ScreeningReportRow) => {
    await this.runReviewAction(row, 'return', () => this.screeningReportService.returnToQueue(row.screeningId), 'Returned to Needs Review');
  }

  formatDate(date: string | undefined, includeTime: boolean = false): string {
    return dateFormat(date, this.userContextProvider.region, includeTime);
  }

  reservationDates(row: ScreeningReportRow): string {
    const arrival = this.formatDate(row.arrivalDate);
    const departure = this.formatDate(row.departureDate);
    return departure ? `${arrival} - ${departure}` : arrival;
  }

  private updateFilterPill(event: ActiveEventMessage): void {
    const definition = this.filterDefinitions.find(d => d.id === event.filterId);
    if (!definition) {
      return;
    }
    if (event.active) {
      this.filterPills.set(event.filterId, {
        filterId: event.filterId,
        text: event.value ? `${definition.shortName} : ${event.value}` : definition.shortName
      });
    } else {
      this.filterPills.delete(event.filterId);
    }
  }

  private async runReviewAction(row: ScreeningReportRow, reviewAction: ReviewAction, action: () => Promise<void>, successMessage: string,
    errorMessages: Record<number, string> = {}): Promise<void> {
    const index = Math.max(this.rows.findIndex(r => r.screeningId === row.screeningId), 0);
    this.pendingAction = reviewAction;
    try {
      await action();
      this.notificationService.showNotification({ message: successMessage });
      await this.reloadAndSelectIndex(index);
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 409) {
        this.notificationService.showNotification({ message: 'This screening is no longer a Consider' });
        await this.reloadAndSelectIndex(index);
      } else if (e instanceof HttpErrorResponse && errorMessages[e.status]) {
        this.notificationService.showNotification({ message: errorMessages[e.status] });
      } else {
        this.logService.handleError(e, 'Failed to update the screening', `Failed to update screening ${row.screeningId} ${e}`);
      }
    } finally {
      this.pendingAction = null;
    }
  }

  // After a row leaves the list, keep the admin's place: select whatever now sits at the same index.
  private async reloadAndSelectIndex(index: number): Promise<void> {
    await this.loadPage();
    if (this.rows.length === 0 && this.pageIndex > 0) {
      this.pageIndex--;
      await this.loadPage();
      this.selectedRow = this.rows[this.rows.length - 1];
      return;
    }
    this.selectedRow = this.rows.length > 0 ? this.rows[Math.min(index, this.rows.length - 1)] : undefined;
  }

  private async loadPage(): Promise<void> {
    const requestId = ++this.latestRequestId;
    this.loading$.next(true);
    try {
      const { rows, totalRecords } = await this.screeningReportService.search(this.buildRequest());
      if (requestId !== this.latestRequestId) {
        return;
      }
      this.rows = rows;
      this.totalRecords = totalRecords;
    } catch (e) {
      if (requestId === this.latestRequestId) {
        // Don't leave the previous tab's or filter's rows on screen under the new tab.
        this.rows = [];
        this.totalRecords = 0;
        this.selectedRow = undefined;
        this.logService.handleError(e, 'Failed to load the screening report', `Failed to load screening report ${e}`);
      }
    } finally {
      if (requestId === this.latestRequestId) {
        this.loading$.next(false);
      }
    }
  }

  private buildRequest(): ScreeningReportSearchRequest {
    const request: ScreeningReportSearchRequest = {
      page: this.pageIndex,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortDirection: this.sortDirection,
      queue: this.queue
    };
    if (this.organizationId) {
      request.organizationId = this.organizationId;
    }
    if (this.appliedKeyword) {
      request.keyword = this.appliedKeyword;
    }
    if (this.appliedDateFilter) {
      request.dateField = this.appliedDateFilter.dateField;
      if (this.appliedDateFilter.from) {
        request.from = this.appliedDateFilter.from;
      }
      if (this.appliedDateFilter.to) {
        request.to = this.appliedDateFilter.to;
      }
    }
    return request;
  }
}
